// GPL (D3, R-30) : gaz en vrac en kilogrammes, bouteilles pleines et vides par format et par site, agrégat du
// gaz en kilogrammes seulement (vrac plus contenu des pleines ; les vides ne portent aucun gaz), remplissages,
// livraisons de bouteilles, inventaires, alertes de pénurie par province. Un nombre de bouteilles n'est jamais
// présenté comme un volume.

import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CODES_PROVINCES, formaterNombre } from '@carburanttrack/shared';
import { api, ErreurApi, messageErreur } from '../../api/client';
import { useSession } from '../../app/session';
import { Message, MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { dateHeure, jourLong, libelleProvince } from '../../composants/format';
import { EtiquetteNiveau, maintenantLocal, localVersIso, quantite, tonDeNiveau } from './composants';
import type { FicheGpl, SiteGpl } from './types';

interface GazNational {
  sites: number;
  totalKg: string;
  base: string;
  detail: { installationId: string; provinceCode: string | null; totalKg: string }[];
}

const LIBELLES_FORMATS: Record<string, string> = { B6: 'Bouteille de 6 kg', B12_5: 'Bouteille de 12,5 kg' };
const LIBELLES_MOUVEMENTS_GPL: Record<string, string> = { remplissage: 'Remplissage', livraison: 'Livraison de bouteilles', inventaire: 'Inventaire' };
const libelleFormat = (f: string | null) => (f ? (LIBELLES_FORMATS[f] ?? f) : 'vrac');

export function PageGpl() {
  const { etat } = useSession();
  const sites = useQuery({ queryKey: ['gpl', 'sites'], queryFn: async () => (await api<SiteGpl[]>('/api/v1/gpl/sites')).donnees, retry: false });
  const lecteurAgregats = etat?.profil === 'ministere' || etat?.profil === 'administration';
  const national = useQuery({ queryKey: ['gpl', 'national'], queryFn: async () => (await api<GazNational>('/api/v1/gpl/national')).donnees, enabled: lecteurAgregats, retry: false });
  if (sites.isLoading) return <Squelette lignes={6} libelle="Chargement du GPL" />;
  if (sites.data && sites.data.length === 1 && !lecteurAgregats) return <Navigate to={`/gpl/${sites.data[0]!.id}`} replace />;
  return (
    <div className="page-gpl">
      <div className="page-tete">
        <div>
          <span className="contexte">{jourLong()}</span>
          <h1>Gaz butane (GPL)</h1>
          <p>Le gaz s’additionne en kilogrammes : vrac des sphères et contenu des bouteilles pleines. Les bouteilles vides ne portent aucun gaz.</p>
        </div>
      </div>
      {national.data && (
        <section className="carte-bloc" aria-labelledby="titre-gaz-national">
          <h2 id="titre-gaz-national">Gaz disponible, national</h2>
          <div className="grille-produits">
            <div className="carte-produit">
              <span className="nom">Total en kilogrammes</span>
              <span className="grand">{quantite(national.data.totalKg, 'kg')}</span>
              <small>
                {national.data.sites} site{national.data.sites > 1 ? 's' : ''} d’emplissage. {national.data.base}.
              </small>
            </div>
          </div>
        </section>
      )}
      {sites.error && sites.error instanceof ErreurApi && sites.error.statut === 403 ? (
        <p className="attenue">Le détail par centre d’emplissage n’est pas lisible avec vos habilitations : seul l’agrégat national est affiché.</p>
      ) : (
        sites.error && <MessageErreur erreur={sites.error} titre="Sites GPL indisponibles" />
      )}
      {sites.data && sites.data.length === 0 && <p className="message info">Aucun centre d’emplissage dans votre périmètre.</p>}
      {sites.data && sites.data.length > 0 && (
        <section className="carte-bloc" aria-labelledby="titre-sites">
          <h2 id="titre-sites">Centres d’emplissage</h2>
          <ul className="liste-lignes">
            {sites.data.map((s) => (
              <li key={s.id}>
                <Link to={`/gpl/${s.id}`}>
                  <span className="pictogramme">
                    <Icone nom="cuve" />
                  </span>
                  <span className="texte">
                    <b>{s.nom}</b>
                    <small>
                      {s.organisationNom}, {libelleProvince(s.provinceCode)} : {quantite(s.gaz.totalKg, 'kg')} de gaz, dont vrac {quantite(s.vracComptableKg, 'kg')}
                    </small>
                  </span>
                  <Icone nom="chevron" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export function PageSiteGpl() {
  const { id } = useParams();
  const fiche = useQuery({ queryKey: ['gpl', 'site', id], queryFn: async () => (await api<FicheGpl>(`/api/v1/gpl/sites/${id}`)).donnees });
  const [panneau, setPanneau] = useState<'remplissage' | 'livraison' | 'inventaire' | null>(null);
  if (fiche.isLoading) return <Squelette lignes={8} libelle="Chargement du site GPL" />;
  if (fiche.error) return <MessageErreur erreur={fiche.error} titre="Site GPL indisponible" />;
  const f = fiche.data!;
  return (
    <div className="page-site-gpl">
      <div className="page-tete">
        <div>
          <span className="contexte">
            {f.installation.organisationNom}, {libelleProvince(f.installation.provinceCode)}, {jourLong()}
          </span>
          <h1>{f.installation.nom}</h1>
          <p>Gaz disponible : {quantite(f.gaz.totalKg, 'kg')} (vrac {quantite(f.gaz.vracKg, 'kg')}, contenu des pleines {quantite(f.gaz.contenuPleinesKg, 'kg')})</p>
        </div>
        <div className="actions">
          {f.droits.remplir && (
            <button type="button" className="bouton primaire" onClick={() => setPanneau('remplissage')}>
              Enregistrer un remplissage
            </button>
          )}
          {f.droits.remplir && (
            <button type="button" className="bouton" onClick={() => setPanneau('livraison')}>
              Enregistrer une livraison
            </button>
          )}
          {f.droits.inventaire && (
            <button type="button" className="bouton" onClick={() => setPanneau('inventaire')}>
              Inventaire des bouteilles
            </button>
          )}
          <Link className="bouton discret" to={`/stocks/installations/${f.installation.id}`}>
            Sphères et jaugeages
          </Link>
        </div>
      </div>

      {panneau && <FormulaireGpl type={panneau} fiche={f} fermer={() => setPanneau(null)} />}

      {f.alertes.length > 0 && (
        <section aria-labelledby="titre-alertes-gpl" className="espace-bas">
          <h2 id="titre-alertes-gpl" className="sur-titre">
            À traiter d’abord
          </h2>
          <ol className="liste-exceptions">
            {f.alertes.map((a, i) => (
              <li key={a.id}>
                <Link className={`exception ${tonDeNiveau(a.niveau)}`} to={`/alertes/${a.id}`}>
                  <span className="numero" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span className="texte">
                    <span className="marque-exception">
                      <EtiquetteNiveau niveau={a.niveau} />
                    </span>
                    <b>{a.titre}</b>
                    <small>{a.description}</small>
                  </span>
                  <Icone nom="chevron" />
                </Link>
              </li>
            ))}
          </ol>
        </section>
      )}

      <div className="grille-produits">
        <div className="carte-produit">
          <span className="nom">Vrac comptable</span>
          <span className="grand">{quantite(f.vracComptableKg, 'kg')}</span>
          <small>Somme des sphères selon les mouvements validés</small>
        </div>
        <div className={`carte-produit${f.vracPhysiqueKg === null ? ' insuffisante' : ''}`}>
          <span className="nom">Vrac jaugé</span>
          <span className="grand">{f.vracPhysiqueKg === null ? 'indisponible' : quantite(f.vracPhysiqueKg, 'kg')}</span>
          <small>{f.motifVracPhysique ?? 'Dernier jaugeage des sphères'}</small>
        </div>
        <div className="carte-produit">
          <span className="nom">Contenu des bouteilles pleines</span>
          <span className="grand">{quantite(f.gaz.contenuPleinesKg, 'kg')}</span>
          <small>{formaterNombre(f.gaz.bouteillesVides)} bouteilles vides, sans gaz</small>
        </div>
      </div>

      <section className="carte-bloc" aria-labelledby="titre-bouteilles">
        <h2 id="titre-bouteilles">Bouteilles par format</h2>
        <div className="tableau-conteneur">
          <table className="tableau">
            <caption className="visuellement-cache">Bouteilles pleines et vides par format</caption>
            <thead>
              <tr>
                <th scope="col">Format</th>
                <th scope="col" className="nombre">
                  Pleines
                </th>
                <th scope="col" className="nombre">
                  Vides
                </th>
                <th scope="col" className="nombre">
                  Gaz des pleines
                </th>
              </tr>
            </thead>
            <tbody>
              {f.bouteilles.map((b) => (
                <tr key={b.format}>
                  <td>{libelleFormat(b.format)}</td>
                  <td className="nombre">{formaterNombre(b.pleines)}</td>
                  <td className="nombre">{formaterNombre(b.vides)}</td>
                  <td className="nombre">{quantite(String(b.pleines * Number(b.contenuKg)), 'kg')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="carte-bloc" aria-labelledby="titre-historique-gpl">
        <h2 id="titre-historique-gpl">Derniers mouvements</h2>
        <div className="tableau-conteneur">
          <table className="tableau">
            <caption className="visuellement-cache">Mouvements GPL</caption>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Opération</th>
                <th scope="col" className="nombre">
                  Pleines
                </th>
                <th scope="col" className="nombre">
                  Vides
                </th>
                <th scope="col" className="nombre">
                  Gaz
                </th>
              </tr>
            </thead>
            <tbody>
              {f.historique.map((h) => (
                <tr key={h.id}>
                  <td>{dateHeure(h.horodatageEvenement)}</td>
                  <td>
                    {LIBELLES_MOUVEMENTS_GPL[h.type] ?? h.type}, {libelleFormat(h.formatCode)}
                    <small>{h.destinataireNom ? `${h.destinataireNom}${h.provinceDestination ? `, ${libelleProvince(h.provinceDestination)}` : ''}` : (h.motif ?? '')}</small>
                  </td>
                  <td className="nombre">{h.pleinesDelta > 0 ? `+${formaterNombre(h.pleinesDelta)}` : formaterNombre(h.pleinesDelta)}</td>
                  <td className="nombre">{h.videsDelta > 0 ? `+${formaterNombre(h.videsDelta)}` : formaterNombre(h.videsDelta)}</td>
                  <td className="nombre">{quantite(h.gazKg, 'kg')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function FormulaireGpl({ type, fiche, fermer }: { type: 'remplissage' | 'livraison' | 'inventaire'; fiche: FicheGpl; fermer: () => void }) {
  const client = useQueryClient();
  const destinataires = useQuery({ queryKey: ['gpl', 'destinataires'], queryFn: async () => (await api<{ id: string; nom: string }[]>('/api/v1/gpl/destinataires')).donnees, enabled: type === 'livraison' });
  const [format, setFormat] = useState(fiche.bouteilles[0]?.format ?? 'B12_5');
  const [n1, setN1] = useState('');
  const [n2, setN2] = useState('');
  const [destinataireId, setDestinataireId] = useState('');
  const [province, setProvince] = useState(fiche.installation.provinceCode ?? '');
  const [motif, setMotif] = useState('');
  const [quand, setQuand] = useState(maintenantLocal());
  const envoi = useMutation({
    mutationFn: async () => {
      const base = `/api/v1/gpl/sites/${fiche.installation.id}`;
      if (type === 'remplissage') return api(`${base}/remplissages`, { corps: { format, nombre: Number(n1), horodatageEvenement: localVersIso(quand) } });
      if (type === 'livraison') return api(`${base}/livraisons`, { corps: { format, pleinesSortantes: Number(n1), videsEntrantes: Number(n2 || 0), destinataireId, provinceDestination: province || null, horodatageEvenement: localVersIso(quand) } });
      return api(`${base}/inventaires`, { corps: { format, pleines: Number(n1), vides: Number(n2), motif } });
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['gpl'] });
      void client.invalidateQueries({ queryKey: ['stock'] });
      setN1('');
      setN2('');
    },
  });
  const titres = { remplissage: 'Enregistrer un remplissage', livraison: 'Enregistrer une livraison de bouteilles', inventaire: 'Inventaire des bouteilles' };
  return (
    <form
      className="panneau-saisie"
      onSubmit={(e) => {
        e.preventDefault();
        envoi.mutate();
      }}
    >
      <h2>{titres[type]}</h2>
      {type === 'remplissage' && <p className="attenue">Le remplissage diminue le vrac du contenu nominal et transforme des bouteilles vides en pleines.</p>}
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="g-format">Format</label>
          <select id="g-format" value={format} onChange={(e) => setFormat(e.target.value)}>
            {fiche.bouteilles.map((b) => (
              <option key={b.format} value={b.format}>
                {libelleFormat(b.format)}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="g-n1">{type === 'remplissage' ? 'Bouteilles remplies' : type === 'livraison' ? 'Pleines sortantes' : 'Pleines comptées'}</label>
          <input id="g-n1" inputMode="numeric" value={n1} onChange={(e) => setN1(e.target.value)} required pattern="[0-9]+" />
        </div>
        {type !== 'remplissage' && (
          <div className="champ">
            <label htmlFor="g-n2">{type === 'livraison' ? 'Vides entrantes' : 'Vides comptées'}</label>
            <input id="g-n2" inputMode="numeric" value={n2} onChange={(e) => setN2(e.target.value)} required={type === 'inventaire'} pattern="[0-9]+" />
          </div>
        )}
        {type !== 'inventaire' && (
          <div className="champ">
            <label htmlFor="g-quand">Date et heure</label>
            <input id="g-quand" type="datetime-local" value={quand} onChange={(e) => setQuand(e.target.value)} required />
          </div>
        )}
      </div>
      {type === 'livraison' && (
        <div className="formulaire-ligne">
          <div className="champ">
            <label htmlFor="g-destinataire">Distributeur destinataire</label>
            <select id="g-destinataire" value={destinataireId} onChange={(e) => setDestinataireId(e.target.value)} required>
              <option value="">Choisir</option>
              {(destinataires.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.nom}
                </option>
              ))}
            </select>
          </div>
          <div className="champ">
            <label htmlFor="g-province">Province de destination</label>
            <select id="g-province" value={province} onChange={(e) => setProvince(e.target.value)}>
              {CODES_PROVINCES.map((c) => (
                <option key={c} value={c}>
                  {libelleProvince(c)}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      {type === 'inventaire' && (
        <div className="champ">
          <label htmlFor="g-motif">Motif (obligatoire)</label>
          <input id="g-motif" value={motif} onChange={(e) => setMotif(e.target.value)} required minLength={3} />
        </div>
      )}
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {envoi.isSuccess && <Message type="succes">Opération enregistrée et tracée.</Message>}
      <div className="actions">
        <button type="submit" className="bouton primaire" disabled={envoi.isPending}>
          Enregistrer
        </button>
        <button type="button" className="bouton" onClick={fermer}>
          Fermer
        </button>
      </div>
    </form>
  );
}
